import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const microeconomicsUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Imperfect Competition",
  examWeight: "15–22%",
  focus: "Production Choices and Behavior (PRD) & Market Inefficiency (POL)",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Introduction to Imperfectly Competitive Markets",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Market Structures Overview</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In the real world, few markets are "Perfectly Competitive." Most firms have some degree of <strong>Market Power</strong>—the ability to set the price above the marginal cost.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse mb-4">
                <thead className="bg-slate-100 text-slate-800">
                  <tr>
                    <th className="p-2 border">Market Structure</th>
                    <th className="p-2 border"># of Firms</th>
                    <th className="p-2 border">Product Type</th>
                    <th className="p-2 border">Barriers</th>
                    <th className="p-2 border">Price Control</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr>
                    <td className="p-2 border font-bold">Perfect Competition</td>
                    <td className="p-2 border">Many</td>
                    <td className="p-2 border">Identical</td>
                    <td className="p-2 border">None</td>
                    <td className="p-2 border">None (Taker)</td>
                  </tr>
                  <tr>
                    <td className="p-2 border font-bold">Monopolistic Comp.</td>
                    <td className="p-2 border">Many</td>
                    <td className="p-2 border">Differentiated</td>
                    <td className="p-2 border">Low</td>
                    <td className="p-2 border">Some</td>
                  </tr>
                  <tr>
                    <td className="p-2 border font-bold">Oligopoly</td>
                    <td className="p-2 border">Few</td>
                    <td className="p-2 border">Identical/Diff.</td>
                    <td className="p-2 border">High</td>
                    <td className="p-2 border">Significant</td>
                  </tr>
                  <tr>
                    <td className="p-2 border font-bold">Monopoly</td>
                    <td className="p-2 border">One</td>
                    <td className="p-2 border">Unique</td>
                    <td className="p-2 border">Very High</td>
                    <td className="p-2 border">Significant (Maker)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 The Demand Curve for Imperfect Competitors</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Imperfect competitors face a <strong>downward-sloping Market Demand curve</strong>. Because they must lower the price to sell more units, the following occurs:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Quantity Effect:</strong> One more unit sold increases total revenue by the price at which it is sold.</li>
                <li><strong>Price Effect:</strong> To sell that last unit, the price must be lowered on <em>all</em> previous units sold.</li>
                <li><strong>The Result:</strong> Marginal Revenue (<InlineMath math="MR" />) is <strong>less</strong> than Price (<InlineMath math="D" />) for every unit after the first. The <InlineMath math="MR" /> curve lies below Demand and is steeper.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Elasticity and Total Revenue (TR)</h3>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Elastic Range</h4>
                <p className="text-xs text-slate-700 mt-1">When <InlineMath math="MR > 0" />. Lowering price increases <InlineMath math="TR" />.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">Unit Elastic</h4>
                <p className="text-xs text-slate-700 mt-1">When <InlineMath math="MR = 0" />. <InlineMath math="TR" /> is maximized.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Inelastic Range</h4>
                <p className="text-xs text-slate-700 mt-1">When <InlineMath math="MR < 0" />. Lowering price decreases <InlineMath math="TR" />.</p>
              </div>
            </div>
            <ImportantNote>
              A monopoly will <strong>never voluntarily produce in the inelastic range</strong> because <InlineMath math="MR" /> is negative (revenue drops) while <InlineMath math="MC" /> is positive (costs rise).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Monopoly",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Characteristics and Barriers to Entry</h3>
            <p className="text-slate-700 mb-4">A single firm is the sole producer of a product with no close substitutes.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">1. Geography/Resources</h5>
                <p className="text-xs mt-1">Owning the only source of an input (e.g., a diamond mine).</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">2. Government/Legal</h5>
                <p className="text-xs mt-1">Patents, copyrights, and licenses.</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">3. Network Externalities</h5>
                <p className="text-xs mt-1">High value because everyone else uses it (e.g., OS software).</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">4. Economies of Scale</h5>
                <p className="text-xs mt-1">Natural Monopoly conditions.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Profit Maximization Graph</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-2">Steps to Solve:</h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Find Quantity (<InlineMath math="Q_m" />):</strong> Where <InlineMath math="MR = MC" />.</li>
                <li><strong>Find Price (<InlineMath math="P_m" />):</strong> Go <strong>UP</strong> from <InlineMath math="Q_m" /> to the <strong>Demand Curve</strong>.</li>
                <li><strong>Find Cost (<InlineMath math="ATC" />):</strong> Go from <InlineMath math="Q_m" /> to the <InlineMath math="ATC" /> curve.</li>
                <li><strong>Calculate Profit:</strong> <InlineMath math="\text{Area} = (P - ATC) \times Q" />.</li>
              </ol>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Inefficiency of Monopoly</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Allocative Inefficiency" 
                definition="Occurs because P > MC. The firm restricts output below the socially optimal level to keep prices high."
              />
              <KeyTerm 
                term="Productive Inefficiency" 
                definition="Occurs because the firm does not produce at the minimum of the ATC curve (P ≠ min ATC)."
              />
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Deadweight Loss (DWL)</h4>
                <p className="text-sm text-slate-700 mt-1">Represented by the triangle pointing right between <InlineMath math="Q_m" /> and the intersection of <InlineMath math="MC" /> and <InlineMath math="D" />.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💧 Natural Monopoly</h3>
            <p className="text-slate-700 text-sm mb-4">
              Occurs when <InlineMath math="LRATC" /> falls continuously as output increases due to extremely high fixed costs (e.g., utility pipes).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-3 rounded border border-slate-300">
                <h5 className="font-bold text-slate-900">Unregulated</h5>
                <p className="text-xs">Produces where <InlineMath math="MR = MC" />. Highest price, lowest quantity.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-3 rounded border border-teal-200">
                <h5 className="font-bold text-teal-900">Fair-Return Price (<InlineMath math="P = ATC" />)</h5>
                <p className="text-xs">The firm breaks even (Normal Profit). No subsidy needed.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-3 rounded border border-purple-200">
                <h5 className="font-bold text-purple-900">Socially Optimal Price (<InlineMath math="P = MC" />)</h5>
                <p className="text-xs">Allocative efficiency. Usually leads to a loss, requiring a <strong>government subsidy</strong>.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Price Discrimination",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎟️ Definition and Conditions</h3>
            <p className="text-slate-700 mb-4">Charging different prices to different consumers for the same good, not based on cost differences.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-3 bg-white border border-slate-200 rounded shadow-sm text-center">
                <h5 className="font-bold text-slate-800 text-xs uppercase">1. Market Power</h5>
                <p className="text-[10px] text-slate-500">Must be a price maker</p>
              </div>
              <div className="p-3 bg-white border border-slate-200 rounded shadow-sm text-center">
                <h5 className="font-bold text-slate-800 text-xs uppercase">2. Segmentation</h5>
                <p className="text-[10px] text-slate-500">Separate by willingness to pay</p>
              </div>
              <div className="p-3 bg-white border border-slate-200 rounded shadow-sm text-center">
                <h5 className="font-bold text-slate-800 text-xs uppercase">3. No Arbitrage</h5>
                <p className="text-[10px] text-slate-500">Must prevent resale</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ Perfect (First-Degree) Price Discrimination</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 space-y-3">
              <p className="text-sm text-slate-700">Firm charges each customer the maximum price they are willing to pay.</p>
              <div className="bg-white p-3 rounded border border-indigo-100">
                <h5 className="font-bold text-indigo-900 text-sm">Graphical Changes:</h5>
                <ul className="list-disc pl-5 text-xs text-slate-600 mt-1">
                  <li>The <strong>Demand Curve becomes the MR Curve</strong> (<InlineMath math="D = MR" />).</li>
                  <li>Produces where <InlineMath math="P = MC" /> (Socially Optimal Quantity).</li>
                </ul>
              </div>
              <div className="bg-white p-3 rounded border border-indigo-100">
                <h5 className="font-bold text-indigo-900 text-sm">Implications:</h5>
                <ul className="list-disc pl-5 text-xs text-slate-600 mt-1">
                  <li><strong>Consumer Surplus = 0</strong> (All captured as profit).</li>
                  <li><strong>Allocative Efficiency:</strong> Surprisingly efficient; <strong>No Deadweight Loss</strong>.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Monopolistic Competition",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🍔 Characteristics</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Many Sellers:</strong> Hundreds of firms (e.g., fast food, salons).</li>
              <li><strong>Differentiated Products:</strong> Not identical; allows for some price control.</li>
              <li><strong>Non-Price Competition:</strong> Focus on advertising, branding, and location.</li>
              <li><strong>Low Barriers:</strong> Easy entry and exit.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Long-Run Adjustment Mechanism</h3>
            <p className="text-slate-700 mb-4">Economic profit must equal <strong>zero</strong> in the long run.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-900 text-sm">If Short-Run Profits:</h5>
                <p className="text-xs text-slate-600 mt-1">Firms enter → Demand for existing firms <strong>decreases (shifts left)</strong> → More elastic → Entry stops when <InlineMath math="P = ATC" />.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-900 text-sm">If Short-Run Losses:</h5>
                <p className="text-xs text-slate-600 mt-1">Firms exit → Demand for remaining firms <strong>increases (shifts right)</strong> → Less elastic → Exit stops when <InlineMath math="P = ATC" />.</p>
              </div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
              <h5 className="font-bold text-slate-900 text-sm">Long-Run Equilibrium:</h5>
              <p className="text-xs text-slate-700 mt-1">The <strong>Tangency Point</strong> where <InlineMath math="MR = MC" /> and Demand is tangent to <InlineMath math="ATC" /> (<InlineMath math="P = ATC" />).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📏 Inefficiency and Excess Capacity</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Allocative Inefficiency</h4>
                <p className="text-xs text-slate-700"><InlineMath math="P > MC" /> (markup charging).</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Excess Capacity</h4>
                <p className="text-xs text-slate-700">Firm produces less than the output that minimizes <InlineMath math="ATC" />. They produce on the downward slope of the <InlineMath math="ATC" /> curve.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Oligopoly and Game Theory",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Oligopoly Characteristics</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Few Large Firms:</strong> Market dominated by 3-4 firms.</li>
              <li><strong>Mutual Interdependence:</strong> Key feature—pricing/output decisions directly affect rivals' profits.</li>
              <li><strong>Collusion:</strong> Incentives to act as a monopoly (Cartels), though often illegal.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎮 Game Theory</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
              <h4 className="font-bold text-slate-900">Reading a 2x2 Payoff Matrix:</h4>
              <p className="text-sm text-slate-700">
                Player 1 (Left) payoffs are the <strong>first</strong> number. Player 2 (Top) payoffs are the <strong>second</strong> number.
              </p>
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <div className="bg-white p-3 rounded border border-slate-300">
                  <h5 className="font-bold text-blue-800 text-xs">Dominant Strategy</h5>
                  <p className="text-[11px] text-slate-600">The best move regardless of the opponent's choice.</p>
                </div>
                <div className="bg-white p-3 rounded border border-slate-300">
                  <h5 className="font-bold text-blue-800 text-xs">Nash Equilibrium</h5>
                  <p className="text-[11px] text-slate-600">Where neither player has an incentive to switch unilaterally.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚔 The Prisoner’s Dilemma</h3>
            <p className="text-slate-700 text-sm mb-4">
              A scenario where rational self-interest leads to a suboptimal outcome. Firms have a dominant strategy to compete/cheat, ending with lower profits than if they had colluded.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Summary of Efficiency</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 border">Structure</th>
                    <th className="p-2 border">Allocative (<InlineMath math="P=MC" />)</th>
                    <th className="p-2 border">Productive (<InlineMath math="P=\text{min ATC}" />)</th>
                    <th className="p-2 border">LR Profit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2 border font-bold">Perfect Comp.</td>
                    <td className="p-2 border text-green-600">Yes (LR)</td>
                    <td className="p-2 border text-green-600">Yes (LR)</td>
                    <td className="p-2 border">Zero</td>
                  </tr>
                  <tr>
                    <td className="p-2 border font-bold">Monopolistic Comp.</td>
                    <td className="p-2 border text-red-600">No</td>
                    <td className="p-2 border text-red-600">No</td>
                    <td className="p-2 border">Zero</td>
                  </tr>
                  <tr>
                    <td className="p-2 border font-bold">Monopoly</td>
                    <td className="p-2 border text-red-600">No</td>
                    <td className="p-2 border text-red-600">No</td>
                    <td className="p-2 border text-green-600">Positive</td>
                  </tr>
                  <tr>
                    <td className="p-2 border font-bold">Price Discrim. Monop.</td>
                    <td className="p-2 border text-green-600">Yes</td>
                    <td className="p-2 border text-red-600">No</td>
                    <td className="p-2 border text-green-600">Positive</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default microeconomicsUnit4Notes;